import { diContainer } from '@/coreContainer/container';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { i18n } from '@/i18n';
import { ref, readonly } from 'vue';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import { useInventoryStore } from '@/InventoryControlModule/pages/useInventoryStore';

/**
 * メモ編集の状態管理用インターフェース
 */
interface MemoEditState {
  /** ローディング状態 */
  status: LoadingState['status'];
  /** 操作対象のデータタイプ */
  dataType: 'aggregateBusiness' | 'business' | 'none';
  /** 削除操作かどうか */
  isDelete: boolean;
}

/**
 * テーブルメモ機能を管理するComposable
 * @description
 * 在庫管理テーブルのメモ機能を統一管理します。
 * 集計業務メモと子取引メモの両方に対応し、
 * 保存・削除・状態管理を行います。
 */
export const useTableMemo = () => {
  const t = i18n.t;
  const inventoryStore = useInventoryStore();

  // メモ編集状態の管理
  const _memoEditState = ref<MemoEditState>({
    status: { type: 'none' },
    dataType: 'none',
    isDelete: false,
  });

  /**
   * メモ操作結果を処理する内部関数
   * @param resultStatus - 操作結果のステータス
   * @param resultCode - 操作結果コード
   * @param isDelete - 削除操作かどうか
   */
  const _handleMemoResult = (resultStatus: 'success' | 'error', resultCode?: string, isDelete = false) => {
    switch (resultStatus) {
      case 'success':
        switch (resultCode) {
          case 'NOT_FOUND_CONTENT':
            _memoEditState.value.status = {
              type: 'error',
              errorMessage: 'データが見つかりませんでした',
            };
            return;
          default:
            _memoEditState.value.status = { type: 'success' };
            toast.success(isDelete ? t('削除に成功しました。') : t('保存しました。'));
        }
        // 状態をリセット
        _memoEditState.value = {
          status: { type: 'none' },
          dataType: 'none',
          isDelete: false,
        };
        break;
      case 'error':
        _memoEditState.value.status = {
          type: 'error',
          errorMessage: 'エラーが発生しました',
        };
        toast.error(isDelete ? t('削除に失敗しました。') : t('保存に失敗しました。'));
        break;
    }
  };

  /**
   * 集計業務メモを保存する
   * @param memoText - 保存するメモテキスト
   * @param businessId - 集計業務ID
   */
  const saveAggregateMemo = async (memoText: string, businessId: string): Promise<void> => {
    _memoEditState.value = {
      status: { type: 'loading' },
      dataType: 'aggregateBusiness',
      isDelete: false,
    };

    try {
      const result = await diContainer.putAggregateBusinessMemoUseCase.Execute(businessId, memoText);
      const resultCode = result.status.type === 'success' ? result.status.data : undefined;
      if (result.status.type === 'success' && resultCode !== 'NOT_FOUND_CONTENT') {
        inventoryStore.updateMemoText(businessId, memoText, 'aggregateBusiness');
      }
      _handleMemoResult(result.status.type, resultCode, false);
    } catch (error) {
      _handleMemoResult('error', undefined, false);
    }
  };

  /**
   * 集計業務メモを削除する
   * @param businessId - 集計業務ID
   */
  const deleteAggregateMemo = async (businessId: string): Promise<void> => {
    _memoEditState.value = {
      status: { type: 'loading' },
      dataType: 'aggregateBusiness',
      isDelete: true,
    };

    try {
      const result = await diContainer.deleteAggregateBusinessMemoUseCase.Execute(businessId);
      const resultCode = result.status.type === 'success' ? result.status.data : undefined;
      if (result.status.type === 'success' && resultCode !== 'NOT_FOUND_CONTENT') {
        inventoryStore.updateMemoText(businessId, undefined, 'aggregateBusiness');
      }
      _handleMemoResult(result.status.type, resultCode, true);
    } catch (error) {
      _handleMemoResult('error', undefined, true);
    }
  };

  /**
   * 子取引メモを保存する
   * @param memoText - 保存するメモテキスト
   * @param businessId - 取引ID
   */
  const saveChildMemo = async (memoText: string, businessId: string): Promise<void> => {
    _memoEditState.value = {
      status: { type: 'loading' },
      dataType: 'business',
      isDelete: false,
    };

    try {
      const result = await diContainer.putBusinessMemoUseCase.Execute(businessId, memoText);
      const resultCode = result.status.type === 'success' ? result.status.data : undefined;
      if (result.status.type === 'success' && resultCode !== 'NOT_FOUND_CONTENT') {
        inventoryStore.updateMemoText(businessId, memoText, 'business');
      }
      _handleMemoResult(result.status.type, resultCode, false);
    } catch (error) {
      _handleMemoResult('error', undefined, false);
    }
  };

  /**
   * 子取引メモを削除する
   * @param businessId - 取引ID
   */
  const deleteChildMemo = async (businessId: string): Promise<void> => {
    _memoEditState.value = {
      status: { type: 'loading' },
      dataType: 'business',
      isDelete: true,
    };

    try {
      const result = await diContainer.deleteBusinessMemoUseCase.Execute(businessId);
      const resultCode = result.status.type === 'success' ? result.status.data : undefined;
      if (result.status.type === 'success' && resultCode !== 'NOT_FOUND_CONTENT') {
        inventoryStore.updateMemoText(businessId, undefined, 'business');
      }
      _handleMemoResult(result.status.type, resultCode, true);
    } catch (error) {
      _handleMemoResult('error', undefined, true);
    }
  };

  return {
    /** 集計業務メモを保存 */
    saveAggregateMemo,
    /** 集計業務メモを削除 */
    deleteAggregateMemo,
    /** 子取引メモを保存 */
    saveChildMemo,
    /** 子取引メモを削除 */
    deleteChildMemo,
    /** メモ編集状態（読み取り専用） */
    memoEditState: readonly(_memoEditState),
  };
};
